/**
 * Device detection utilities for server-side rendering
 */

// Device size constants - keeping local to avoid circular dependencies
const DEVICE_SIZES = {
  LARGE: "lg",
  SMALL: "sm",
} as const;

type DeviceSize = typeof DEVICE_SIZES.LARGE | typeof DEVICE_SIZES.SMALL;

/**
 * Detects device type from Akamai x-akamai-device-characteristics header
 *
 * @param headers - Request headers object
 * @returns Device size ("lg" | "sm") or null if header not present
 *
 * @example
 * ```typescript
 * import { detectDeviceFromAkamaiHeader } from "@tm/utility";
 *
 * export async function getServerSideProps(context) {
 *   const { req } = context;
 *   const deviceType = detectDeviceFromAkamaiHeader(req.headers);
 *
 *   return {
 *     props: { deviceType }
 *   };
 * }
 * ```
 */
export function detectDeviceFromAkamaiHeader(
  headers: Record<string, string | string[]> = {}
): DeviceSize | null {
  const akamaiDeviceHeader = headers["x-akamai-device-characteristics"];

  if (!akamaiDeviceHeader) {
    return null;
  }

  // Handle both string and string[] cases
  const headerValue = Array.isArray(akamaiDeviceHeader)
    ? akamaiDeviceHeader[0]
    : akamaiDeviceHeader;

  if (headerValue?.includes("is_mobile=true")) {
    return DEVICE_SIZES.SMALL;
  } else {
    return DEVICE_SIZES.LARGE;
  }
}

// Export the constants for convenience
export { DEVICE_SIZES };
